import { MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Modal, Space } from "antd";
import {
  useUpdatePaymentAllocationsFeePlanMutation,
  useUpdateCreditedCollectorMutation,
  useUpdateAllocatedCreditorMutation,
  useRefreshFeePlanRulesetMutation,
} from "features/payments/paymentsAllocations";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import PaymentAllocationBreakdownModal from "pages/agencyPortal/payments/paymentAllocationBreakdownModal";
import PaymentAllocationCollectorModal from "pages/agencyPortal/payments/paymentAllocationColletorModal";
import PaymentAllocationCreditorModal from "pages/agencyPortal/payments/paymentAllocationCreditorModal";
import PaymentAllocationFeePlanModal from "pages/agencyPortal/payments/paymentAllocationFeePlanModal";
import { useState } from "react";

export default function PaymentAllocationBulkActions({ records = [] }) {
  const [isCreditorModalVisible, setCreditorModalVisible] = useState(false);
  const [isForwardingEntityModalVisible, setForwardingEntityModalVisible] = useState(false);
  const [paymentAllocationRecord, setPaymentAllocationRecord] = useState(null);
  const [isUpdateCreditedCollectorModalVisible, setUpdateCreditedCollectorModalVisible] =
    useState(false);
  const [isUpdateAllocatedCreditorModalVisible, setUpdateAllocatedCreditorModalVisible] =
    useState(false);
  const [updateFeePlans] = useUpdatePaymentAllocationsFeePlanMutation();
  const [updateCreditedCollector] = useUpdateCreditedCollectorMutation();
  const [updateAllocatedCreditor] = useUpdateAllocatedCreditorMutation();
  const [refreshFeePlanRuleset] = useRefreshFeePlanRulesetMutation();
  const {
    data: { [PERMISSIONS.PAYMENT__REALLOCATE]: isPaymentReallocateAuthorized },
  } = useAuthorizations();
  const onUpdateCreditedCollector = async (data) => {
    const result = await updateCreditedCollector({
      ...data,
      updateCreditedCollector: true,
      paymentAllocationIds: records.map((record) => record.id),
    });
    if ("data" in result) {
      setUpdateCreditedCollectorModalVisible(false);
    }
  };
  const onUpdateAllocatedCreditor = async (data) => {
    const result = await updateAllocatedCreditor({
      ...data,
      paymentAllocationIds: records.map((record) => record.id),
    });
    if ("data" in result) {
      setUpdateAllocatedCreditorModalVisible(false);
    }
  };

  const items = [
    {
      key: "updateCreditorFeePlan",
      label: "Update Creditor Fee Plan",
      isVisible: true,
      disabled: records.length === 0,
    },
    {
      key: "updateForwardingEntityFeePlan",
      label: "Update Forwarding Entity Fee Plan",
      isVisible: true,
      disabled: records.length === 0,
    },
    {
      key: "updateCreditedCollector",
      label: "Update Credited Collector",
      isVisible: true,
      disabled: records.length === 0,
    },
    {
      key: "updateAllocatedCreditor",
      label: "Update Allocated Creditor",
      isVisible: true,
      disabled: records.length === 0,
    },
    {
      key: "refreshFeePlanRuleset",
      label: "Recalculate fee plan",
      isVisible: true,
      disabled: records.length === 0,
    },
    {
      key: "updatePaymentDistribution",
      label: "Update Payment Distribution",
      disabled: records.length === 0 || records.length > 1,
      isVisible: isPaymentReallocateAuthorized,
    },
  ];

  const actionHandlers = {
    updateCreditorFeePlan: () => {
      setCreditorModalVisible(true);
    },
    updateForwardingEntityFeePlan: () => {
      setForwardingEntityModalVisible(true);
    },
    updateCreditedCollector: () => {
      setUpdateCreditedCollectorModalVisible(true);
    },
    updateAllocatedCreditor: () => {
      setUpdateAllocatedCreditorModalVisible(true);
    },
    refreshFeePlanRuleset: () => {
      Modal.confirm({
        title: "Recalculate fee plan",
        content: `Are you sure you want to recalculate fee plan for ${records.length} payment allocation(s)?`,
        icon: null,
        onOk: async () => {
          const result = await refreshFeePlanRuleset({
            paymentAllocationIds: records.map((record) => record.id),
          });
          return result;
        },
      });
    },
    updatePaymentDistribution: async () => {
      setPaymentAllocationRecord(records[0]);
    },
  };
  return (
    <>
      <Space size="small">
        <Dropdown
          menu={{
            // @ts-ignore
            items: items.filter((item) => item.isVisible),
            onClick: (clickHandlerProp) => actionHandlers[clickHandlerProp.key](),
          }}
          trigger={["click"]}
        >
          <Button
            data-testid="payment-allocations-bulk-actions"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            type="text"
          >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </Space>
      {isCreditorModalVisible && (
        <PaymentAllocationFeePlanModal
          open={isCreditorModalVisible}
          onOk={(data) => {
            updateFeePlans({
              ...data,
              updateCreditorFeePlan: true,
              paymentAllocationIds: records.map((record) => record.id),
            });
            setCreditorModalVisible(false);
          }}
          onCancel={() => setCreditorModalVisible(false)}
        />
      )}
      {isForwardingEntityModalVisible && (
        <PaymentAllocationFeePlanModal
          open={isForwardingEntityModalVisible}
          onOk={(data) => {
            updateFeePlans({
              ...data,
              updateForwardingEntityFeePlan: true,
              paymentAllocationIds: records.map((record) => record.id),
            });
            setForwardingEntityModalVisible(false);
          }}
          onCancel={() => setForwardingEntityModalVisible(false)}
        />
      )}
      {isUpdateCreditedCollectorModalVisible && (
        <PaymentAllocationCollectorModal
          open={isUpdateCreditedCollectorModalVisible}
          onOk={onUpdateCreditedCollector}
          onCancel={() => setUpdateCreditedCollectorModalVisible(false)}
        />
      )}
      {isUpdateAllocatedCreditorModalVisible && (
        <PaymentAllocationCreditorModal
          open={isUpdateAllocatedCreditorModalVisible}
          onOk={onUpdateAllocatedCreditor}
          onCancel={() => setUpdateAllocatedCreditorModalVisible(false)}
        />
      )}
      {paymentAllocationRecord && (
        <PaymentAllocationBreakdownModal
          paymentIntentId={paymentAllocationRecord.paymentIntentId}
          title="Payment Distribution"
          onCancel={() => setPaymentAllocationRecord(null)}
          onOk={() => setPaymentAllocationRecord(null)}
        />
      )}
    </>
  );
}
